import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import { get, isNaN, isNull, map } from 'lodash';
import { default as React } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { QuantificationFormValues } from './types';
import { defaultsSyntheticSegmentationConfiguration, setDefaultSyntheticSegmentationValues } from './utils';

export interface SyntheticSegmentationConfigurationStepProps {
  methods: UseFormReturn<QuantificationFormValues, any>;
  checkValidationAndSetIsStepFailed: () => void;
}

export const SyntheticSegmentationConfigurationStep: React.FC<SyntheticSegmentationConfigurationStepProps> = ({
  methods,
  checkValidationAndSetIsStepFailed,
}) => {
  const {
    control,
    formState: { errors },
  } = methods;

  const onChangeDefaultSyntheticSegmentation = (caseIndex: number) => {
    setDefaultSyntheticSegmentationValues(caseIndex, methods);
    checkValidationAndSetIsStepFailed();
  };

  const onChangeNumberField = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    fieldOnChange: (...event: any[]) => void
  ) => {
    const value = event.target.value;
    if (value === '') {
      fieldOnChange(null);
    } else {
      const parsed = parseFloat(value);
      fieldOnChange(isNaN(parsed) ? null : parsed);
    }
    checkValidationAndSetIsStepFailed();
  };

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item mb={2}>
        <Controller
          control={control}
          name="configParams.syntheticSegmentationConfigurationDefaultCase"
          render={({ field: { onChange, value } }) => {
            return (
              <FormControl fullWidth>
                <FormLabel id="choose-compare-option-label">Defaults Synthetic Segmentation:</FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="choose-compare-option-label"
                  name="choose-compare-option-group"
                  value={value}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    onChange(e);
                    onChangeDefaultSyntheticSegmentation(Number(e.target.value));
                  }}
                >
                  <Grid container direction="column">
                    {map(defaultsSyntheticSegmentationConfiguration, (caseValue, caseIndex) => {
                      return (
                        <Grid item key={caseIndex}>
                          <FormControlLabel
                            key={caseIndex}
                            value={caseIndex}
                            control={<Radio />}
                            label={caseValue.name}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </RadioGroup>
              </FormControl>
            );
          }}
        />
      </Grid>

      <Grid item>
        <Controller
          control={control}
          name="configParams.cellCompartmentParams.membraneOutwardsThicknessUm"
          render={({ field: { onChange, value } }) => {
            return (
              <TextField
                label="Membrane Outwards Thickness (um)"
                type="number"
                value={isNull(value) ? '' : value}
                onChange={(event) => {
                  onChangeNumberField(event, onChange);
                }}
                error={Boolean(get(errors, 'configParams.cellCompartmentParams.membraneOutwardsThicknessUm'))}
                helperText={
                  get(errors, 'configParams.cellCompartmentParams.membraneOutwardsThicknessUm')?.message ||
                  'Specifies the outward extent (in microns) of the membrane region relative to the cytoplasmic boundary. This defines how far the synthetic membrane extends beyond the cell boundary, used for generating the outer portion of the membrane compartment in downstream analyses'
                }
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position="end">um</InputAdornment>,
                }}
                placeholder="Membrane Outwards Thickness (um)"
              />
            );
          }}
        />
      </Grid>
      <Grid item>
        <Controller
          control={control}
          name="configParams.cellCompartmentParams.membraneInwardsThicknessUm"
          render={({ field: { onChange, value } }) => {
            return (
              <TextField
                label="Membrane Inwards Thickness (um)"
                type="number"
                value={isNull(value) ? '' : value}
                onChange={(event) => {
                  onChangeNumberField(event, onChange);
                }}
                error={Boolean(get(errors, 'configParams.cellCompartmentParams.membraneInwardsThicknessUm'))}
                helperText={
                  get(errors, 'configParams.cellCompartmentParams.membraneInwardsThicknessUm')?.message ||
                  'Specifies the inward extent (in microns) of the membrane region relative to the cytoplasmic boundary. This defines how far the synthetic membrane extends into the cytoplasm, used for generating the inner portion of the membrane compartment in downstream analyses'
                }
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position="end">um</InputAdornment>,
                }}
                placeholder="Membrane Inwards Thickness (um)"
              />
            );
          }}
        />
      </Grid>
      <Grid item>
        <Controller
          control={control}
          name="configParams.syntheticCytoplasmThicknessUm"
          render={({ field: { onChange, value } }) => (
            <TextField
              label="Synthetic Cytoplasm Thickness (um)"
              type="number"
              value={isNull(value) ? '' : value}
              onChange={(event) => {
                onChangeNumberField(event, onChange);
              }}
              error={Boolean(get(errors, 'configParams.syntheticCytoplasmThicknessUm'))}
              helperText={
                get(errors, 'configParams.syntheticCytoplasmThicknessUm')?.message ||
                'Specifies the thickness (in microns) used to generate a synthetic cytoplasm by buffering the nuclear geometry.This is applied when nuclear geometry exists but cell geometry is missing'
              }
              fullWidth
              InputProps={{
                endAdornment: <InputAdornment position="end">um</InputAdornment>,
              }}
            />
          )}
        />
      </Grid>

      <Grid item>
        <Controller
          control={control}
          name="configParams.syntheticNuclearRadiusUm"
          render={({ field: { onChange, value } }) => (
            <TextField
              label="Synthetic Nuclear Radius (um)"
              type="number"
              value={isNull(value) ? '' : value}
              onChange={(event) => {
                onChangeNumberField(event, onChange);
              }}
              error={Boolean(get(errors, 'configParams.syntheticNuclearRadiusUm'))}
              helperText={
                get(errors, 'configParams.syntheticNuclearRadiusUm')?.message ||
                'Specifies the radius (in microns) used to generate a synthetic nuclear geometry as a circle around the cell center. This is applied when both nuclear and cell geometries are missing'
              }
              fullWidth
              InputProps={{
                endAdornment: <InputAdornment position="end">um</InputAdornment>,
              }}
            />
          )}
        />
      </Grid>

      <Grid item>
        <Controller
          control={control}
          name="configParams.syntheticMembraneErosionFactor"
          render={({ field: { onChange, value } }) => (
            <TextField
              label="Synthetic Membrane Erosion Factor"
              type="number"
              value={isNull(value) ? '' : value}
              onChange={(event) => {
                onChangeNumberField(event, onChange);
              }}
              error={Boolean(get(errors, 'configParams.syntheticMembraneErosionFactor'))}
              helperText={
                get(errors, 'configParams.syntheticMembraneErosionFactor')?.message ||
                'Specifies the erosion factor used to derive a synthetic nuclear geometry by shrinking the existing cell geometry. This is applied when nuclear geometry is missing but cell geometry is available'
              }
              fullWidth
            />
          )}
        />
      </Grid>
    </Grid>
  );
};
